'use client'

import { FC, FormEvent } from 'react'
import type { Route } from 'next'
import { type UseFormReturnType } from '@mantine/form'
import { Paper, PasswordInput, TextInput, Title } from '@mantine/core'
import { Button, Flex, Link } from '@/common'
import { SignInError } from './sign-in-error'
import { fontWeight, semanticColor } from '@/theme/tokens'

export type SignInFormValues = {
    email: string
    password: string
}

export type SignInFormViewProps = {
    form: UseFormReturnType<SignInFormValues>
    onSubmit: (event?: FormEvent<HTMLFormElement>) => void
    forgotPasswordHref: Route
    clerkError: { title: string; message: string } | null
    setClerkError: (error: { title: string; message: string } | null) => void
}

export const SignInFormView: FC<SignInFormViewProps> = ({
    form,
    onSubmit,
    forgotPasswordHref,
    clerkError,
    setClerkError,
}) => {
    return (
        <form onSubmit={onSubmit}>
            <Paper bg={semanticColor('surface.raised')} radius="sm" p="xxl">
                <Title mb="lg" order={3} ta="center">
                    Welcome to SafeInsights!
                </Title>
                <Flex direction="column" gap="xs">
                    <TextInput
                        key={form.key('email')}
                        {...form.getInputProps('email')}
                        label="Email"
                        placeholder="Enter your registered email address"
                        aria-label="Email"
                    />
                    <PasswordInput
                        label="Password"
                        key={form.key('password')}
                        {...form.getInputProps('password')}
                        mt={10}
                        placeholder="*********"
                        aria-label="Password"
                        // Mantine renders PasswordInput's inner <input> with withAria disabled, so
                        // its own `error` never sets `aria-invalid` (OTTER-647).
                        aria-invalid={!!form.errors.password || undefined}
                    />
                    <Link
                        c={semanticColor('link.default')}
                        fw={fontWeight.semibold}
                        w="fit-content"
                        size="xs"
                        href={forgotPasswordHref}
                    >
                        Forgot password?
                    </Link>
                    <SignInError clerkError={clerkError} setClerkError={setClerkError} />
                    <Button
                        mt="md"
                        mb="xxl"
                        size="lg"
                        disabled={!form.isValid()}
                        type="submit"
                        bg={!form.isValid() ? 'grey.1' : undefined}
                    >
                        Login
                    </Button>
                </Flex>
            </Paper>
        </form>
    )
}
